// A profile (design/DIRECTION.md §11, after iOS FS-148, the reader's own page): a room (one of the
// house paintings, chosen by the person) with their name and face set on glass over it; then bio,
// where they are and their church tradition when they've given them, and their friends in words.
// On the reader's own page, a Reading tab: dials that fill for their own plans (never zero, never
// compared), and their three most recent notes. No follower number, no stat tiles, no streaks.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { STUDY } from "../../components/AppShell";
import { Art, type ArtName } from "../../components/Art";
import { Avatar } from "../../components/Avatar";
import { AvatarStack } from "../../components/AvatarStack";
import { Button, LinkButton } from "../../components/Button";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Dial } from "../../components/Dial";
import { EmptyState } from "../../components/Empty";
import { Icon } from "../../components/Icon";
import { Menu } from "../../components/Menu";
import { Post } from "../../components/Post";
import { PostList } from "../../components/PostList";
import { ReportDialog } from "../../components/ReportDialog";
import { SectionHeader } from "../../components/SectionHeader";
import { Skeleton } from "../../components/Skeleton";
import { TabPanel, Tabs } from "../../components/Tabs";
import ui from "../../components/ui.module.css";
import { useJourneys } from "../../data/planQueries";
import { useFriendAction, useFriends, useProfile, useProfilePosts } from "../../data/queries";
import type { Post as PostT, Profile } from "../../data/types";
import { useTitle } from "../../lib/useTitle";
import { morphName } from "../../motion";
import s from "./profile.module.css";

export const Route = createFileRoute("/_app/@{$handle}")({ component: ProfilePage });

const ROOMS: { name: ArtName; focus: string }[] = [
  { name: "friedrich", focus: "50% 30%" },
  { name: "ruisdael", focus: "50% 30%" },
  { name: "cole", focus: "50% 35%" },
];
const roomFor = (id: string) => ROOMS[[...id].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 3) % ROOMS.length]!;

/**
 * Facts in a row split by hairlines (after the profile card in the inspirations, with facts where it
 * had ratings): where they are and their church, as they chose to show them (FS-136), and their
 * friends in words. Never a score.
 */
function Facts({ profile, friends }: { profile: Profile; friends: ReactNode }) {
  const d = profile.details;
  const place = [d?.city, d?.country].filter(Boolean).join(", ");
  const faith = [d?.denomination, d?.church].filter(Boolean).join(", ");
  const facts = [place ? { text: place, label: "Lives in" } : null, faith ? { text: faith, label: "Church" } : null].filter(
    (f): f is NonNullable<typeof f> => f !== null,
  );
  return (
    <ul className={s.facts}>
      {facts.map((f) => (
        <li key={f.label} className={s.fact}>
          <span className={s.factLabel}>{f.label}</span>
          <span className={s.factValue}>{f.text}</span>
        </li>
      ))}
      {friends && <li className={`${s.fact} ${s.factFriends}`}>{friends}</li>}
    </ul>
  );
}

type Tab = "posts" | "reading";

function ProfilePage() {
  const { handle } = Route.useParams();
  const { data: profile, isPending } = useProfile(handle);
  const { data: posts } = useProfilePosts(handle);
  const friend = useFriendAction();
  const [reporting, setReporting] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [tab, setTab] = useState<Tab>("posts");
  const navigate = useNavigate();
  useTitle(profile ? profile.displayName : "Profile");

  if (isPending)
    return (
      <div className={ui.page} aria-busy="true">
        <Skeleton shape="block" height={220} className={s.waiting} />
        <Skeleton width="40%" height={14} style={{ marginTop: 24 }} />
      </div>
    );
  if (!profile)
    return (
      <div className={ui.page}>
        <EmptyState title="There’s no one here by that name." body="Handles can change. Search for them on Friends.">
          <LinkButton to="/friends" search={{ tab: "find" }} variant="secondary">
            Find people
          </LinkButton>
        </EmptyState>
      </div>
    );

  const self = profile.relation === "self";
  const canSee = self || profile.relation === "friends";
  const act = (action: "request" | "accept" | "decline" | "remove" | "block") => friend.mutate({ action, userId: profile.id, handle: profile.handle });
  const room = roomFor(profile.id);
  const first = profile.displayName.split(" ")[0];

  const postsView = canSee ? (
    posts && posts.length > 0 ? (
      <PostList posts={posts} label={self ? "your posts" : `${profile.displayName}’s posts`} />
    ) : (
      <EmptyState compact title={self ? "You haven’t shared anything yet." : `${first} hasn’t shared anything yet.`}>
        {self && (
          <LinkButton to="/new" variant="primary" icon="plus">
            Share a verse
          </LinkButton>
        )}
      </EmptyState>
    )
  ) : (
    <EmptyState compact icon="lock" title="Posts are shared with friends." body={`Add ${first} to see what they’re reading.`} />
  );

  return (
    <div className={s.page} data-surface="cards">
      {/* The cover card: their room fading into the paper, the face overlapping it, then who they are. */}
      <header className={s.cover}>
        <div className={s.coverArt}>
          <Art name={room.name} focus={room.focus} decorative priority arrive shape="none" className={s.art} />
        </div>
        <div className={s.identity}>
          {/* The twin of the face pressed in a list: the browser morphs one into the other (src/motion/shared.ts). */}
          <span className={s.face} style={{ viewTransitionName: morphName("avatar", profile.id) }}>
            <Avatar user={profile} size={96} />
          </span>
          <div className={s.who}>
            <h1 className={s.name}>{profile.displayName}</h1>
            <p className={s.handle}>@{profile.handle}</p>
          </div>
        <div className={s.actions}>
          {self ? (
            <LinkButton to="/settings" variant="primary" icon="settings">
              Edit profile
            </LinkButton>
          ) : profile.relation === "friends" ? (
            <span className={s.state}>
              <Icon name="people.fill" size={18} />
              Friends
            </span>
          ) : profile.relation === "requested" ? (
            <Button variant="secondary" icon="check" onClick={() => act("decline")}>
              Requested
            </Button>
          ) : profile.relation === "incoming" ? (
            <Button variant="primary" onClick={() => act("accept")}>
              Accept request
            </Button>
          ) : (
            <Button variant="primary" icon="user-plus" onClick={() => act("request")}>
              Add friend
            </Button>
          )}
          {!self && (
            <Menu
              label={`More about ${profile.displayName}`}
              items={[
                ...(profile.relation === "friends" ? [{ label: "Remove friend", icon: "user-minus" as const, onSelect: () => act("remove") }] : []),
                { label: "Report", icon: "flag", destructive: true, onSelect: () => setReporting(true) },
                { label: "Block", icon: "block", destructive: true, onSelect: () => setBlocking(true) },
              ]}
            />
          )}
        </div>
        </div>
        {profile.bio && <p className={s.bio}>{profile.bio}</p>}
        <Facts
          profile={profile}
          friends={self ? <MyFriends /> : <span className={s.factValue}>{profile.friendCount === 1 ? "1 friend" : `${profile.friendCount} friends`}</span>}
        />
      </header>

      {self && (
        <nav className={s.study} aria-label="Study">
          <ul>
            {STUDY.map((x) => (
              <li key={x.to}>
                <Link to={x.to} className={s.studyLink}>
                  <Icon name={x.icon} size={18} />
                  {x.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <div className={s.posts}>
        {self ? (
          <>
            <Tabs
              label="Your page"
              idPrefix="profile"
              value={tab}
              onChange={setTab}
              items={[
                { id: "posts", label: "Posts" },
                { id: "reading", label: "Reading" },
              ]}
            />
            <TabPanel idPrefix="profile" value={tab} index={tab === "posts" ? 0 : 1}>
              {tab === "posts" ? postsView : <Reading posts={posts ?? []} />}
            </TabPanel>
          </>
        ) : (
          postsView
        )}
      </div>
      {blocking && (
        <ConfirmDialog
          title={`Block ${profile.displayName}?`}
          body="You won’t see each other’s posts, comments or profile anywhere in Fellowship, and you’ll stop being friends. They won’t be told."
          confirm="Block"
          onConfirm={() => friend.mutate({ action: "block", userId: profile.id }, { onSuccess: () => void navigate({ to: "/home" }) })}
          onClose={() => setBlocking(false)}
        />
      )}
      {reporting && <ReportDialog target={{ kind: "user", id: profile.id }} name={profile.displayName} onClose={() => setReporting(false)} />}
    </div>
  );
}

/** The reader's own friends, as faces and names: who they read with, never a number to grow. */
function MyFriends() {
  const { data } = useFriends();
  const friends = data?.friends ?? [];
  if (friends.length === 0) return null;
  const named = friends.slice(0, 2).map((u) => u.displayName.split(" ")[0]);
  const rest = friends.length - named.length;
  return (
    <Link to="/friends" className={s.friends}>
      <AvatarStack users={friends} max={4} size={28} />
      <span>
        Friends with {named.join(rest > 0 ? ", " : " and ")}
        {rest > 0 && ` and ${rest} more`}
      </span>
    </Link>
  );
}

/** Your reading: a dial for each plan under way (days read of its length), then recent notes. */
function Reading({ posts }: { posts: PostT[] }) {
  const { data: journeys = [] } = useJourneys();
  const going = journeys.filter((j) => j.me.read.length > 0).slice(0, 3);
  const notes = posts.filter((p) => p.kind === "note").slice(0, 3);
  return (
    <div className={s.reading}>
      <section aria-labelledby="plans-head">
        <SectionHeader
          id="plans-head"
          title="Plans under way"
          action={
            <LinkButton to="/plans" variant="text" size="sm" iconEnd="forward">
              All plans
            </LinkButton>
          }
        />
        {going.length > 0 ? (
          <ul className={s.dials}>
            {going.map((j) => (
              <li key={j.id}>
                <Link to="/plans/$journeyId" params={{ journeyId: j.id }} className={s.dialLink}>
                  <Dial value={j.me.read.length} max={j.plan.days} label="Days read" size={96} />
                  <span className={s.dialTitle}>{j.plan.title}</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className={s.quiet}>
            When you begin a plan, how far you’ve come shows here, for you alone.{" "}
            <Link to="/plans" className={s.inline}>
              Find a plan
            </Link>
          </p>
        )}
      </section>
      <section aria-labelledby="notes-head" className={s.notes}>
        <SectionHeader id="notes-head" title="Recent notes" />
        {notes.length > 0 ? (
          notes.map((p) => <Post key={p.id} post={p} />)
        ) : (
          <p className={s.quiet}>
            Notes you share with friends gather here.{" "}
            <Link to="/new" search={{ kind: "note" }} className={s.inline}>
              Write a note
            </Link>
          </p>
        )}
      </section>
    </div>
  );
}
